import * as THREE from "three";
import { bus } from "../core/bus";
import type { NavWorld } from "../world/navmesh";
import { seekToward } from "./tactics";
import { nearestAliveIndex } from "./perception";
import { SoldierInstance, type SoldierAsset } from "./soldier-glb";
import { buildSoldier, animateSoldier, type SoldierRig } from "./soldier-geometry";
import type { Squad } from "./squad";
import type { EnemyAnchorDef, WaveDef } from "../mission/types";
import { terrainHeightAt } from "../world/terrain";

const ENEMY_HP = 3;
const ADVANCE_SPEED = 4.8;
const ENGAGE_RANGE = 80;
const FIRE_INTERVAL_MIN = 2.0;
const FIRE_INTERVAL_MAX = 3.5;
const HIT_CHANCE = 0.30;
const IFF_CONE_DOT = 0.985;

const REINFORCE_INTERVAL_BASE = 28;
const REINFORCE_COUNT_BASE = 2;
const REINFORCE_ESCALATION = 0.035;
const REINFORCE_MAX_ALIVE = 18;
const REINFORCE_SPREAD = 30;
// Ambient reinforcement is finite so clear-all objectives stay winnable.
// Missions override via reinforcementBudget in their JSON.
const REINFORCE_DEFAULT_BUDGET = 24;
const DEFAULT_ACTIVATE_RADIUS = 135;
const DEFAULT_TETHER_RADIUS = 150;
// Defensive enemies that drift past tether * this factor walk back home
// instead of chasing; the gap between 1.0 and this keeps the boundary from
// oscillating advance/return every frame.
const TETHER_RETURN_FACTOR = 1.25;
// Enemies re-run their line-of-sight raycast at most this often (plus jitter)
// while a target is in range — full-scene raycasts every frame would be
// wasted work for a value that changes slowly.
const LOS_RECHECK_INTERVAL = 0.65;
// Minimum spacing between live enemies; mirrors the squad's separation so
// converging attackers stop stacking inside one another.
const ENEMY_SEPARATION_DISTANCE = 2.4;
// Corpses linger this long before their meshes leave the scene — with
// endless reinforcement, never culling them grows draw calls, shadow cost,
// and per-frame iteration without bound.
const CORPSE_LINGER_SECONDS = 8;
const DEG_TO_RAD = Math.PI / 180;

export type LosQuery = (from: THREE.Vector3, to: THREE.Vector3) => boolean;

type RoleProfile = {
  initiallyActivated: boolean;
  engageRange: number;
  hitChance: number;
  advanceSpeed: number;
  tetherRadius: number;
  deploymentDelay: number;
};

type EnemyState = "holding" | "advancing" | "engaging" | "down";

type SpawnAnchor = EnemyAnchorDef & {
  activated: boolean;
  spent: boolean;
};

type Enemy = {
  soldier: SoldierInstance | null;
  rig: SoldierRig | null;
  soldierGroup: THREE.Group;
  hp: number;
  state: EnemyState;
  fireTimer: number;
  isTopThreat: boolean;
  prevMoving: boolean;
  prevShooting: boolean;
  phase: number;
  homeAnchorId: string;
  source: EnemyAnchorDef["source"];
  posture: NonNullable<EnemyAnchorDef["posture"]>;
  activated: boolean;
  activateRadius: number;
  homePosition: THREE.Vector3;
  engageRange: number;
  hitChance: number;
  advanceSpeed: number;
  tetherRadius: number;
  deploymentTimer: number;
  losTimer: number;
  hasLos: boolean;
  // Navmesh route toward the current movement goal (null → straight line)
  navPath: THREE.Vector3[] | null;
  navPathIdx: number;
  repathTimer: number;
  pathGoal: THREE.Vector3;
  downElapsed: number;
  facingRad?: number;
  fireArcRad?: number;
};

type EnemySectorConfig = {
  facingRad?: number;
  fireArcRad?: number;
};

const _toEnemy = new THREE.Vector3();
const _holdDelta = new THREE.Vector3();
const _sectorDelta = new THREE.Vector3();
const _shotFrom = new THREE.Vector3();
const _shotTo = new THREE.Vector3();
const _losFrom = new THREE.Vector3();
const _losTo = new THREE.Vector3();
const _separation = new THREE.Vector3();
const _pairDelta = new THREE.Vector3();
const _holdOrigin = new THREE.Vector3();
const ENEMY_EMISSIVE = new THREE.Color(0xcc1100);
const VEHICLE_BODY = new THREE.MeshStandardMaterial({ color: 0x6c6c69, roughness: 0.88, metalness: 0.08 });
const VEHICLE_CAB = new THREE.MeshStandardMaterial({ color: 0x4c4f56, roughness: 0.82, metalness: 0.1 });
const VEHICLE_WHEEL = new THREE.MeshStandardMaterial({ color: 0x191919, roughness: 1.0, metalness: 0.02 });
const NEST_SANDBAG = new THREE.MeshStandardMaterial({ color: 0x8b7a55, roughness: 0.96, metalness: 0.02 });
const NEST_GUN = new THREE.MeshStandardMaterial({ color: 0x2b2d2c, roughness: 0.72, metalness: 0.22 });

function defaultAnchorFromWave(wave: WaveDef, index: number): SpawnAnchor {
  const anchor: SpawnAnchor = {
    id: `wave-${index}`,
    source: "fallback",
    trigger: wave.trigger,
    count: wave.count,
    origin: wave.origin,
    spread: wave.spread,
    posture: "assault",
    reinforcementEligible: true,
    activated: true,
    spent: false,
  };

  if (wave.waypointIndex !== undefined) anchor.waypointIndex = wave.waypointIndex;
  if (wave.enemiesAlive !== undefined) anchor.enemiesAlive = wave.enemiesAlive;
  return anchor;
}

function materializeAnchors(waves: WaveDef[], anchorDefs: EnemyAnchorDef[]): SpawnAnchor[] {
  const source = anchorDefs.length > 0 ? anchorDefs : waves.map(defaultAnchorFromWave);
  return source.map((anchor, index) => ({
    ...anchor,
    id: anchor.id || `anchor-${index}`,
    posture: anchor.posture ?? "assault",
    reinforcementEligible: anchor.reinforcementEligible ?? anchor.source !== "nest",
    activated: false,
    spent: false,
  }));
}

function roleProfileForAnchor(anchor: Pick<EnemyAnchorDef, "source" | "posture">): RoleProfile {
  const posture = anchor.posture ?? "assault";

  if (anchor.source === "nest") {
    return {
      initiallyActivated: false,
      engageRange: 135,
      hitChance: 0.42,
      advanceSpeed: 0.8,
      tetherRadius: 12,
      deploymentDelay: 0,
    };
  }

  if (anchor.source === "vehicle") {
    return {
      initiallyActivated: false,
      engageRange: 85,
      hitChance: 0.26,
      advanceSpeed: 5.4,
      tetherRadius: 240,
      deploymentDelay: 1.4,
    };
  }

  if (posture === "garrison") {
    return {
      initiallyActivated: false,
      engageRange: 96,
      hitChance: 0.32,
      advanceSpeed: 4.7,
      // Garrison troops defend their structure — they push out only far
      // enough to bring the approach road into engage range, never chase.
      tetherRadius: 48,
      deploymentDelay: 0,
    };
  }

  if (posture === "ambush") {
    return {
      initiallyActivated: false,
      engageRange: 105,
      hitChance: 0.36,
      advanceSpeed: 5.0,
      // Ambushers spring farther than garrison but still break contact
      // rather than pursuing across the map.
      tetherRadius: 110,
      deploymentDelay: 0,
    };
  }

  return {
    initiallyActivated: true,
    engageRange: ENGAGE_RANGE,
    hitChance: HIT_CHANCE,
    advanceSpeed: ADVANCE_SPEED,
    tetherRadius: DEFAULT_TETHER_RADIUS,
    deploymentDelay: 0,
  };
}

function sectorConfigForAnchor(anchor: Pick<EnemyAnchorDef, "facingDeg" | "fireArcDeg">): EnemySectorConfig {
  const sector: EnemySectorConfig = {};
  if (anchor.facingDeg !== undefined) sector.facingRad = anchor.facingDeg * DEG_TO_RAD;
  if (anchor.fireArcDeg !== undefined) sector.fireArcRad = anchor.fireArcDeg * DEG_TO_RAD;
  return sector;
}

export class EnemyManager {
  readonly group = new THREE.Group();
  private enemies: Enemy[] = [];
  private readonly anchorProps = new Map<string, THREE.Object3D>();
  private totalSpawned = 0;
  private asset: SoldierAsset | undefined;
  private nav: NavWorld | null = null;

  private readonly anchors: SpawnAnchor[];
  private reinforceTimer = REINFORCE_INTERVAL_BASE;
  private readonly reinforcementBudget: number;
  private reinforcementsSpawned = 0;
  private missionElapsed = 0;
  private missionActive = false;

  private readonly onWaypointReached: (payload: { index: number }) => void;
  private readonly onMissionStarted: () => void;

  constructor(
    waves: WaveDef[] = [],
    anchorDefs: EnemyAnchorDef[] = [],
    asset?: SoldierAsset,
    reinforcementBudget: number = REINFORCE_DEFAULT_BUDGET,
    // SUPPRESSOR trait: global multiplier on enemy accuracy.
    private readonly hitChanceMult = 1,
    // ESCALATING modifier: < 1 = ambient reinforcements arrive faster.
    private readonly reinforceIntervalMult = 1,
  ) {
    this.asset = asset;
    this.reinforcementBudget = reinforcementBudget;
    this.anchors = materializeAnchors(waves, anchorDefs);

    this.onMissionStarted = () => {
      this.spawnByTrigger("immediate");
      this.missionActive = true;
      this.missionElapsed = 0;
      this.reinforceTimer = REINFORCE_INTERVAL_BASE;
    };
    this.onWaypointReached = ({ index }) => {
      this.spawnWaypointAnchors(index);
    };

    bus.on("mission:started", this.onMissionStarted);
    bus.on("squad:waypointReached", this.onWaypointReached);
  }

  private spawnByTrigger(trigger: WaveDef["trigger"]): void {
    for (const anchor of this.anchors) {
      if (!anchor.spent && anchor.trigger === trigger) {
        this.spawnAnchor(anchor);
      }
    }
  }

  private spawnWaypointAnchors(index: number): void {
    for (const anchor of this.anchors) {
      if (!anchor.spent && anchor.trigger === "waypointReached" && anchor.waypointIndex === index) {
        this.spawnAnchor(anchor);
      }
    }
  }

  private spawnThresholdAnchors(): void {
    const alive = this.aliveCount;
    for (const anchor of this.anchors) {
      if (anchor.spent || anchor.trigger !== "threshold") continue;
      if (alive <= (anchor.enemiesAlive ?? 0)) this.spawnAnchor(anchor);
    }
  }

  private spawnAnchor(anchor: SpawnAnchor): void {
    const origin = new THREE.Vector3(...anchor.origin);
    const posture = anchor.posture ?? "assault";
    const profile = roleProfileForAnchor(anchor);
    // Spawns always honor the anchor's authored activateRadius — waypoint
    // triggers place enemies in the world, they do not wake them, so nothing
    // pops in already firing on top of the squad.
    const initiallyActivated = profile.initiallyActivated;
    const activateRadius = anchor.activateRadius ?? DEFAULT_ACTIVATE_RADIUS;

    if (!this.anchorProps.has(anchor.id) && anchor.source === "vehicle") {
      this.anchorProps.set(anchor.id, this.addVehicleProp(anchor.id, origin));
    }
    if (!this.anchorProps.has(anchor.id) && anchor.source === "nest") {
      this.anchorProps.set(anchor.id, this.addNestProp(anchor.id, origin, anchor.facingDeg));
    }

    for (let i = 0; i < anchor.count; i++) {
      const offset = anchor.source === "vehicle"
        ? new THREE.Vector3((i - (anchor.count - 1) * 0.5) * 7, 0, -Math.random() * Math.max(anchor.spread, 10))
        : new THREE.Vector3(
          (Math.random() - 0.5) * anchor.spread * 2,
          0,
          (Math.random() - 0.5) * anchor.spread * 2,
        );
      this.spawnEnemy(origin.clone().add(offset), {
        anchorId: anchor.id,
        source: anchor.source,
        posture,
        profile,
        initiallyActivated,
        activateRadius,
        homePosition: origin,
        ...sectorConfigForAnchor(anchor),
      });
    }

    anchor.spent = true;
    anchor.activated = initiallyActivated;
  }

  private addVehicleProp(id: string, origin: THREE.Vector3): THREE.Object3D {
    const truck = new THREE.Group();
    truck.name = `vehicle-anchor:${id}`;
    truck.position.copy(origin);

    const body = new THREE.Mesh(new THREE.BoxGeometry(9, 1.5, 3.8), VEHICLE_BODY);
    body.position.set(0, 1.4, 0.4);
    truck.add(body);

    const bed = new THREE.Mesh(new THREE.BoxGeometry(4.8, 1.2, 3.4), VEHICLE_BODY);
    bed.position.set(-1.6, 2.2, 0.4);
    truck.add(bed);

    const cab = new THREE.Mesh(new THREE.BoxGeometry(3.1, 1.8, 3.1), VEHICLE_CAB);
    cab.position.set(2.4, 2.0, 0.2);
    truck.add(cab);

    for (const [x, z] of [[-2.8, -1.8], [-2.8, 2.2], [2.8, -1.8], [2.8, 2.2]] as const) {
      const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.78, 0.78, 0.7, 14), VEHICLE_WHEEL);
      wheel.rotation.z = Math.PI / 2;
      wheel.position.set(x, 0.72, z);
      truck.add(wheel);
    }

    truck.position.y = terrainHeightAt(origin.x, origin.z);
    truck.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.castShadow = true;
        obj.receiveShadow = true;
      }
    });
    this.group.add(truck);
    return truck;
  }

  private addNestProp(id: string, origin: THREE.Vector3, facingDeg = 0): THREE.Object3D {
    const nest = new THREE.Group();
    nest.name = `nest-anchor:${id}`;
    nest.position.copy(origin);
    nest.rotation.y = facingDeg * DEG_TO_RAD;

    for (let i = 0; i < 7; i++) {
      const angle = -Math.PI * 0.72 + (i / 6) * Math.PI * 1.44;
      const bag = new THREE.Mesh(new THREE.BoxGeometry(2.8, 0.65, 1.1), NEST_SANDBAG);
      bag.position.set(Math.sin(angle) * 5.2, 0.45, Math.cos(angle) * 4.2);
      bag.rotation.y = angle;
      nest.add(bag);
    }

    const shield = new THREE.Mesh(new THREE.BoxGeometry(3.2, 1.3, 0.35), NEST_GUN);
    shield.position.set(0, 1.25, 2.2);
    nest.add(shield);

    const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 4.2, 10), NEST_GUN);
    barrel.rotation.x = Math.PI / 2;
    barrel.position.set(0, 1.35, 4.15);
    nest.add(barrel);

    nest.position.y = terrainHeightAt(origin.x, origin.z);
    nest.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.castShadow = true;
        obj.receiveShadow = true;
      }
    });
    this.group.add(nest);
    return nest;
  }

  private spawnEnemy(
    pos: THREE.Vector3,
    anchorState: {
      anchorId: string;
      source: EnemyAnchorDef["source"];
      posture: NonNullable<EnemyAnchorDef["posture"]>;
      profile: RoleProfile;
      initiallyActivated: boolean;
      activateRadius: number;
      homePosition: THREE.Vector3;
      facingRad?: number;
      fireArcRad?: number;
    },
  ): void {
    let soldierGroup: THREE.Group;
    let soldier: SoldierInstance | null = null;
    let rig: SoldierRig | null = null;

    if (this.asset) {
      soldier = new SoldierInstance(this.asset, ENEMY_EMISSIVE, { isEnemy: true }, 1.0);
      soldierGroup = soldier.group;
    } else {
      const mat = new THREE.MeshStandardMaterial({ color: 0xff2200, emissive: new THREE.Color(0xcc1100), emissiveIntensity: 1.8 });
      rig = buildSoldier(mat, { isEnemy: true });
      soldierGroup = rig.group;
    }

    soldierGroup.position.copy(pos);
    soldierGroup.position.y = terrainHeightAt(pos.x, pos.z);
    if (anchorState.facingRad !== undefined) soldierGroup.rotation.y = anchorState.facingRad;
    soldierGroup.traverse((obj) => {
      if (obj instanceof THREE.Mesh || obj instanceof THREE.SkinnedMesh) {
        obj.castShadow = true;
        obj.receiveShadow = true;
      }
    });
    this.group.add(soldierGroup);
    this.totalSpawned++;
    const enemy: Enemy = {
      soldier,
      rig,
      soldierGroup,
      hp: ENEMY_HP,
      state: anchorState.initiallyActivated && anchorState.profile.deploymentDelay === 0 ? "advancing" : "holding",
      fireTimer: FIRE_INTERVAL_MIN + Math.random() * (FIRE_INTERVAL_MAX - FIRE_INTERVAL_MIN),
      isTopThreat: false,
      prevMoving: false,
      prevShooting: false,
      phase: Math.random() * Math.PI * 2,
      homeAnchorId: anchorState.anchorId,
      source: anchorState.source,
      posture: anchorState.posture,
      activated: anchorState.initiallyActivated,
      activateRadius: anchorState.activateRadius,
      homePosition: anchorState.homePosition.clone(),
      engageRange: anchorState.profile.engageRange,
      hitChance: anchorState.profile.hitChance,
      advanceSpeed: anchorState.profile.advanceSpeed,
      tetherRadius: anchorState.profile.tetherRadius,
      deploymentTimer: anchorState.profile.deploymentDelay,
      losTimer: 0,
      hasLos: true,
      navPath: null,
      navPathIdx: 0,
      repathTimer: 0,
      pathGoal: new THREE.Vector3(1e9, 0, 1e9), // far sentinel forces the first repath
      downElapsed: 0,
    };
    if (anchorState.facingRad !== undefined) enemy.facingRad = anchorState.facingRad;
    if (anchorState.fireArcRad !== undefined) enemy.fireArcRad = anchorState.fireArcRad;
    this.enemies.push(enemy);
  }

  // Spawn infantry outside the anchor system: truck deployments and tank
  // crews bailing out (ai/armor.ts reports through the engine into here).
  spawnLoose(position: THREE.Vector3, count: number, posture: "assault" | "ambush"): void {
    const profile: RoleProfile = posture === "assault"
      ? { initiallyActivated: true, engageRange: 70, hitChance: 0.24, advanceSpeed: 3.6, tetherRadius: 150, deploymentDelay: 0.6 }
      // Bailed crews are shaken: short range, poor aim, hug the wreck
      : { initiallyActivated: true, engageRange: 45, hitChance: 0.12, advanceSpeed: 2.4, tetherRadius: 40, deploymentDelay: 1.2 };
    for (let i = 0; i < count; i++) {
      const off = new THREE.Vector3((Math.random() - 0.5) * 12, 0, (Math.random() - 0.5) * 12);
      this.spawnEnemy(position.clone().add(off), {
        anchorId: "loose",
        source: "fallback",
        posture,
        profile,
        initiallyActivated: true,
        activateRadius: 1e9,
        homePosition: position.clone(),
      });
    }
  }

  topThreatPosition(): THREE.Vector3 | null {
    const t = this.enemies.find((e) => e.isTopThreat && e.state !== "down");
    return t ? t.soldierGroup.position : null;
  }

  private reinforcementAnchors(): SpawnAnchor[] {
    const eligible = this.anchors.filter((anchor) => anchor.reinforcementEligible);
    return eligible.length > 0 ? eligible : this.anchors;
  }

  private spawnReinforcements(hold: { position: THREE.Vector3; radius: number } | null = null): void {
    const budgetLeft = this.reinforcementBudget - this.reinforcementsSpawned;
    if (budgetLeft <= 0) return;

    let anchors = this.reinforcementAnchors();
    // While the squad waits on a holdUntilClear node, don't repopulate the
    // zone it is trying to clear — that combination soft-locks the route.
    if (hold) {
      anchors = anchors.filter((anchor) => {
        _holdOrigin.set(...anchor.origin);
        return _holdOrigin.distanceTo(hold.position) > hold.radius;
      });
    }
    if (anchors.length === 0) return;

    const anchor = anchors[Math.floor(Math.random() * anchors.length)]!;
    const count = Math.min(
      budgetLeft,
      Math.round(REINFORCE_COUNT_BASE + this.missionElapsed * REINFORCE_ESCALATION),
    );
    const origin = new THREE.Vector3(...anchor.origin);
    const spread = Math.max(anchor.spread, REINFORCE_SPREAD);
    const posture = anchor.posture ?? "assault";
    const profile = roleProfileForAnchor(anchor);
    const activateRadius = anchor.activateRadius ?? DEFAULT_ACTIVATE_RADIUS;

    for (let i = 0; i < count; i++) {
      const off = anchor.source === "vehicle"
        ? new THREE.Vector3((i - (count - 1) * 0.5) * 7, 0, -Math.random() * spread)
        : new THREE.Vector3(
          (Math.random() - 0.5) * spread * 2,
          0,
          (Math.random() - 0.5) * spread * 2,
        );
      this.spawnEnemy(origin.clone().add(off), {
        anchorId: anchor.id,
        source: anchor.source,
        posture,
        profile,
        initiallyActivated: true,
        activateRadius,
        homePosition: origin,
        ...sectorConfigForAnchor(anchor),
      });
      this.reinforcementsSpawned++;
    }
  }

  private updateThreatScores(squadPositions: THREE.Vector3[]): void {
    if (squadPositions.length === 0) return;
    let topScore = -Infinity;
    let topEnemy: Enemy | null = null;
    for (const e of this.enemies) {
      if (e.state === "down") { e.isTopThreat = false; continue; }
      const nearestIdx = nearestAliveIndex(e.soldierGroup.position, squadPositions);
      if (nearestIdx < 0) { e.isTopThreat = false; continue; }
      const dist = e.soldierGroup.position.distanceTo(squadPositions[nearestIdx]!);
      const score = 1000 / (dist + 1)
        + (e.state === "engaging" ? 5 : 0)
        + (e.activated ? 2 : 0)
        + (e.source === "nest" ? 3 : 0);
      e.isTopThreat = false;
      if (score > topScore) { topScore = score; topEnemy = e; }
    }
    if (topEnemy) topEnemy.isTopThreat = true;
  }

  private isDefensive(enemy: Enemy): boolean {
    return enemy.posture === "garrison"
      || enemy.posture === "ambush"
      || enemy.source === "structure"
      || enemy.source === "building"
      || enemy.source === "cave";
  }

  private shouldAdvance(enemy: Enemy, dist: number): boolean {
    if (enemy.source === "nest") return false;
    if (enemy.deploymentTimer > 0) return false;
    // In range with a clear line of sight → engage branch owns the enemy.
    // In range but occluded → keep advancing to flush the target out.
    if (dist <= enemy.engageRange && enemy.hasLos) return false;

    if (this.isDefensive(enemy)) {
      // Defensive roles push out only while inside their tether; the engage
      // branch already stops them once the target is in range, so the tether
      // is what makes garrison/ambush read differently from assault.
      const enemyFromHome = enemy.homePosition.distanceTo(enemy.soldierGroup.position);
      return enemyFromHome < enemy.tetherRadius;
    }

    return true;
  }

  private isInsideFireSector(enemy: Enemy, target: THREE.Vector3): boolean {
    if (enemy.source !== "nest" || enemy.facingRad === undefined || enemy.fireArcRad === undefined) return true;
    _sectorDelta.copy(target).sub(enemy.soldierGroup.position);
    _sectorDelta.y = 0;
    if (_sectorDelta.lengthSq() < 0.001) return true;
    const targetAngle = Math.atan2(_sectorDelta.x, _sectorDelta.z);
    const delta = Math.atan2(Math.sin(targetAngle - enemy.facingRad), Math.cos(targetAngle - enemy.facingRad));
    return Math.abs(delta) <= enemy.fireArcRad * 0.5;
  }

  private faceTargetOrSector(enemy: Enemy, target: THREE.Vector3): void {
    if (enemy.source === "nest" && enemy.facingRad !== undefined) {
      enemy.soldierGroup.rotation.y = enemy.facingRad;
      return;
    }

    _holdDelta.copy(target).sub(enemy.soldierGroup.position);
    if (_holdDelta.lengthSq() > 0.001) {
      enemy.soldierGroup.rotation.y = Math.atan2(_holdDelta.x, _holdDelta.z);
    }
  }

  // Accepts every friendly element on the ground (RESCUE fields two: the
  // moving squad and the trapped element). Enemies engage whoever is
  // nearest; damage routes back to the owning squad.
  update(dt: number, squads: Squad | Squad[], losCheck?: LosQuery): void {
    const all = Array.isArray(squads) ? squads : [squads];
    const squad = all[0]!; // route-following element drives reinforcement pacing
    if (this.missionActive) {
      this.missionElapsed += dt;
      this.reinforceTimer -= dt;
      if (this.reinforceTimer <= 0 && this.aliveCount < REINFORCE_MAX_ALIVE) {
        this.reinforceTimer = REINFORCE_INTERVAL_BASE * this.reinforceIntervalMult * (1 - Math.min(0.5, this.missionElapsed * 0.003));
        this.spawnReinforcements(squad.activeHold());
      }
    }

    this.spawnThresholdAnchors();

    const squadPositions: THREE.Vector3[] = [];
    const owners: Squad[] = [];
    for (const s of all) {
      for (const p of s.memberPositions()) {
        squadPositions.push(p);
        owners.push(s);
      }
    }
    this.updateThreatScores(squadPositions);

    for (const e of this.enemies) {
      if (e.state === "down") {
        // Keep the mixer running so death animations finish playing
        e.downElapsed += dt;
        if (e.soldier) e.soldier.update(dt);
        continue;
      }
      if (squadPositions.length === 0) continue;

      const nearestIdx = nearestAliveIndex(e.soldierGroup.position, squadPositions);
      if (nearestIdx < 0) continue;
      const nearestPos = squadPositions[nearestIdx]!;
      const dist = e.soldierGroup.position.distanceTo(nearestPos);

      if (e.deploymentTimer > 0) {
        e.deploymentTimer = Math.max(0, e.deploymentTimer - dt);
        if (e.deploymentTimer === 0) e.activated = true;
      }

      if (!e.activated && dist <= e.activateRadius) {
        e.activated = true;
      }

      let moving = false;
      let shooting = false;

      if (!e.activated) {
        e.state = "holding";
        this.faceTargetOrSector(e, nearestPos);
      } else if (
        dist <= e.engageRange
        && this.isInsideFireSector(e, nearestPos)
        && this.hasLineOfSight(e, nearestPos, dt, losCheck)
      ) {
        shooting = true;
        e.state = "engaging";
        e.fireTimer -= dt;
        if (e.fireTimer <= 0) {
          e.fireTimer = FIRE_INTERVAL_MIN + Math.random() * (FIRE_INTERVAL_MAX - FIRE_INTERVAL_MIN);
          _shotFrom.copy(e.soldierGroup.position).add(new THREE.Vector3(0, 1.35, 0));
          _shotTo.copy(nearestPos).setY(terrainHeightAt(nearestPos.x, nearestPos.z) + 1.1);
          const hit = Math.random() < e.hitChance * this.hitChanceMult;
          bus.emit("enemy:fired", { from: _shotFrom.clone(), to: _shotTo.clone(), hit });
          bus.emit("squad:underFire", { from: _shotFrom.clone(), to: _shotTo.clone(), hit });
          if (hit) owners[nearestIdx]!.takeDamage();
        }
      } else if (this.shouldAdvance(e, dist)) {
        moving = true;
        e.state = "advancing";
        this.moveAlongNav(e, nearestPos, e.advanceSpeed, dt);
      } else if (this.shouldReturnHome(e)) {
        // Defensive stray pushed past its tether (chases, separation shoves) —
        // walk back to the anchor instead of standing in the open.
        moving = true;
        e.state = "advancing";
        this.moveAlongNav(e, e.homePosition, e.advanceSpeed * 0.85, dt);
      } else {
        e.state = "holding";
        this.faceTargetOrSector(e, nearestPos);
      }

      if (moving !== e.prevMoving) {
        e.prevMoving = moving;
        if (e.soldier) e.soldier.setMoving(moving);
      }
      if (shooting !== e.prevShooting) {
        e.prevShooting = shooting;
        if (e.soldier) e.soldier.setShooting(shooting);
      }

      if (e.soldier) {
        e.soldier.update(dt);
      } else if (e.rig) {
        e.phase += dt;
        animateSoldier(e.rig, e.phase, moving);
      }
    }

    this.applyEnemySeparation(dt);
    this.cullDownEnemies();
  }

  // Remove corpses whose linger time expired — meshes leave the scene and the
  // entries leave the array so per-frame iteration and draw calls stay
  // bounded over long missions. totalSpawned is cumulative and unaffected.
  private cullDownEnemies(): void {
    let needsCull = false;
    for (const e of this.enemies) {
      if (e.state === "down" && e.downElapsed >= CORPSE_LINGER_SECONDS) { needsCull = true; break; }
    }
    if (!needsCull) return;
    this.enemies = this.enemies.filter((e) => {
      if (e.state !== "down" || e.downElapsed < CORPSE_LINGER_SECONDS) return true;
      this.group.remove(e.soldierGroup);
      return false;
    });
  }

  setNavWorld(nav: NavWorld | null): void {
    this.nav = nav;
    for (const e of this.enemies) e.navPath = null;
  }

  // Step toward `goal`, routing along the navmesh when one is available.
  // Paths are recomputed on a jittered timer or when the goal has moved
  // meaningfully; failure leaves navPath null → straight-line pursuit.
  private moveAlongNav(e: Enemy, goal: THREE.Vector3, speed: number, dt: number): void {
    if (this.nav) {
      e.repathTimer -= dt;
      const goalMoved = e.pathGoal.distanceToSquared(goal) > 225; // 15 units
      if (!e.navPath || goalMoved || e.repathTimer <= 0) {
        e.navPath = this.nav.findPath(e.soldierGroup.position, goal);
        e.navPathIdx = 0;
        e.pathGoal.copy(goal);
        e.repathTimer = 1.4 + Math.random() * 0.8;
      }
    }

    let step = goal;
    if (e.navPath && e.navPath.length > 0) {
      e.navPathIdx = Math.min(e.navPathIdx, e.navPath.length - 1);
      step = e.navPath[e.navPathIdx]!;
      _pairDelta.copy(step).sub(e.soldierGroup.position);
      _pairDelta.y = 0;
      if (_pairDelta.length() <= 1.5 && e.navPathIdx < e.navPath.length - 1) {
        e.navPathIdx++;
        step = e.navPath[e.navPathIdx]!;
      }
    }

    const newPos = seekToward(e.soldierGroup.position, step, speed, dt);
    e.soldierGroup.position.copy(newPos);
    e.soldierGroup.position.y = terrainHeightAt(newPos.x, newPos.z);
    _pairDelta.copy(step).sub(e.soldierGroup.position);
    if (_pairDelta.x * _pairDelta.x + _pairDelta.z * _pairDelta.z > 0.001) {
      e.soldierGroup.rotation.y = Math.atan2(_pairDelta.x, _pairDelta.z);
    }
  }

  private shouldReturnHome(enemy: Enemy): boolean {
    if (!enemy.activated || !this.isDefensive(enemy)) return false;
    const fromHome = enemy.homePosition.distanceTo(enemy.soldierGroup.position);
    return fromHome > enemy.tetherRadius * TETHER_RETURN_FACTOR;
  }

  // Re-check occlusion on a slow per-enemy cadence; between checks the last
  // answer stands. Without a losCheck (tests, headless) everyone can see.
  private hasLineOfSight(e: Enemy, target: THREE.Vector3, dt: number, losCheck?: LosQuery): boolean {
    if (!losCheck) return true;
    e.losTimer -= dt;
    if (e.losTimer <= 0) {
      e.losTimer = LOS_RECHECK_INTERVAL + Math.random() * 0.5;
      _losFrom.copy(e.soldierGroup.position);
      _losFrom.y += 1.5;
      _losTo.copy(target);
      _losTo.y = terrainHeightAt(target.x, target.z) + 1.1;
      e.hasLos = losCheck(_losFrom, _losTo);
    }
    return e.hasLos;
  }

  // Pairwise push-apart for live ground enemies so converging attackers do
  // not stack inside one another. Nests are fixed emplacements and skip it.
  private applyEnemySeparation(dt: number): void {
    const active = this.enemies.filter((e) => e.state !== "down" && e.source !== "nest");
    for (let i = 0; i < active.length; i++) {
      const enemy = active[i]!;
      _separation.set(0, 0, 0);
      for (let j = 0; j < active.length; j++) {
        if (j === i) continue;
        const other = active[j]!;
        _pairDelta.copy(enemy.soldierGroup.position).sub(other.soldierGroup.position);
        _pairDelta.y = 0;
        const distance = _pairDelta.length();
        if (distance >= ENEMY_SEPARATION_DISTANCE) continue;
        if (distance === 0) {
          // Perfectly stacked (zero-spread spawns) — split along a
          // deterministic per-index direction so the pile can break apart.
          const angle = i * 2.399963; // golden angle keeps directions distinct
          _pairDelta.set(Math.sin(angle), 0, Math.cos(angle));
        } else {
          _pairDelta.normalize();
        }
        _separation.addScaledVector(
          _pairDelta,
          (ENEMY_SEPARATION_DISTANCE - distance) / ENEMY_SEPARATION_DISTANCE,
        );
      }
      if (_separation.lengthSq() === 0) continue;
      enemy.soldierGroup.position.addScaledVector(_separation.normalize(), dt * 1.6);
      enemy.soldierGroup.position.y = terrainHeightAt(enemy.soldierGroup.position.x, enemy.soldierGroup.position.z);
    }
  }

  hit(object: THREE.Object3D): void {
    for (const e of this.enemies) {
      if (e.state === "down") continue;
      let found = false;
      e.soldierGroup.traverse((child) => { if (child === object) found = true; });
      if (found) { this.damage(e, 1); return; }
    }
  }

  // Blast damage: full maxDamage through the inner 55% of the radius, falling
  // linearly to 1 at the rim. Weapons own their maxDamage (howitzer one-shots
  // through the core; autocannon wounds hard); default 1 keeps chaingun
  // fragmentary splash unchanged.
  hitInRadius(center: THREE.Vector3, radius: number, maxDamage = 1): boolean {
    let hit = false;
    for (const e of this.enemies) {
      if (e.state === "down") continue;
      const dist = e.soldierGroup.position.distanceTo(center);
      if (dist > radius) continue;
      const t = dist / radius;
      let damage = t <= 0.55
        ? maxDamage
        : Math.max(1, Math.round(THREE.MathUtils.lerp(maxDamage, 1, (t - 0.55) / 0.45)));
      // Fortified infantry (garrison troops still at their structure) take
      // reduced blast damage — cover matters; precision direct hits bypass
      // this (see hit()).
      if (this.isFortified(e)) damage = Math.max(1, Math.floor(damage * 0.6));
      this.damage(e, damage);
      hit = true;
    }
    return hit;
  }

  private isFortified(e: Enemy): boolean {
    if (e.posture !== "garrison") return false;
    if (e.source !== "structure" && e.source !== "building" && e.source !== "nest") return false;
    return e.soldierGroup.position.distanceTo(e.homePosition) < 20;
  }

  hitNearest(fromPositions: THREE.Vector3[], losCheck?: LosQuery): void {
    const alive = this.enemies.filter((e) => e.state !== "down");
    if (alive.length === 0 || fromPositions.length === 0) return;

    const candidates = alive
      .map((e) => {
        const idx = nearestAliveIndex(e.soldierGroup.position, fromPositions);
        return idx < 0
          ? null
          : { e, from: fromPositions[idx]!, dist: e.soldierGroup.position.distanceTo(fromPositions[idx]!) };
      })
      .filter((c): c is NonNullable<typeof c> => c !== null)
      .sort((a, b) => a.dist - b.dist);

    // The squad shoots the nearest enemy it can actually see. LOS raycasts
    // are capped to the closest few candidates to bound per-tick cost; if
    // everything nearby is behind cover the squad holds fire this proc.
    const maxLosChecks = 4;
    for (let i = 0; i < candidates.length; i++) {
      const candidate = candidates[i]!;
      if (losCheck && i < maxLosChecks) {
        _losFrom.copy(candidate.from);
        _losFrom.y += 1.4;
        _losTo.copy(candidate.e.soldierGroup.position);
        _losTo.y += 1.1;
        if (!losCheck(_losFrom, _losTo)) continue;
      } else if (losCheck) {
        return;
      }
      this.damage(candidate.e, 1);
      return;
    }
  }

  checkEnemyIFF(camPos: THREE.Vector3, camFwd: THREE.Vector3): boolean {
    for (const e of this.enemies) {
      if (e.state === "down") continue;
      _toEnemy.copy(e.soldierGroup.position).sub(camPos).normalize();
      if (_toEnemy.dot(camFwd) > IFF_CONE_DOT) return true;
    }
    return false;
  }

  get aliveCount(): number {
    return this.enemies.filter((e) => e.state !== "down").length;
  }

  alivePositions(): THREE.Vector3[] {
    return this.enemies.filter((e) => e.state !== "down").map((e) => e.soldierGroup.position.clone());
  }

  hasActiveThreatNear(position: THREE.Vector3, radius: number): boolean {
    return this.enemies.some((enemy) => {
      if (enemy.state === "down") return false;
      // Dormant garrisons that haven't noticed the squad don't block advance —
      // only enemies that are awake count as a tactical threat.
      if (!enemy.activated) return false;
      return enemy.soldierGroup.position.distanceTo(position) <= radius;
    });
  }

  get totalEnemiesSpawned(): number {
    return this.totalSpawned;
  }

  debugSnapshot(): Array<{
    source: EnemyAnchorDef["source"];
    posture: NonNullable<EnemyAnchorDef["posture"]>;
    state: EnemyState;
    activated: boolean;
    deploymentTimer: number;
    x: number;
    z: number;
    facingRad?: number;
    fireArcRad?: number;
  }> {
    return this.enemies.map((enemy) => ({
      ...{
        source: enemy.source,
        posture: enemy.posture,
        state: enemy.state,
        activated: enemy.activated,
        deploymentTimer: enemy.deploymentTimer,
        x: enemy.soldierGroup.position.x,
        z: enemy.soldierGroup.position.z,
      },
      ...(enemy.facingRad === undefined ? {} : { facingRad: enemy.facingRad }),
      ...(enemy.fireArcRad === undefined ? {} : { fireArcRad: enemy.fireArcRad }),
    }));
  }

  dispose(): void {
    bus.off("mission:started", this.onMissionStarted);
    bus.off("squad:waypointReached", this.onWaypointReached);
    for (const e of this.enemies) this.group.remove(e.soldierGroup);
    for (const prop of this.anchorProps.values()) this.group.remove(prop);
    this.anchorProps.clear();
  }

  private damage(e: Enemy, amount: number): void {
    e.hp -= amount;
    if (e.soldier) e.soldier.flashHit();

    if (e.hp <= 0 && e.state !== "down") {
      const wasTopThreat = e.isTopThreat;
      e.state = "down";
      e.isTopThreat = false;
      if (e.soldier) {
        e.soldier.die();
      } else {
        e.soldierGroup.rotation.z = Math.PI / 2;
        e.soldierGroup.position.y = terrainHeightAt(e.soldierGroup.position.x, e.soldierGroup.position.z) + 0.3;
        e.soldierGroup.traverse((obj) => {
          const mesh = obj as THREE.Mesh;
          if (mesh.isMesh) (mesh.material as THREE.MeshStandardMaterial).emissiveIntensity = 0;
        });
      }
      bus.emit("enemy:killed", { wasTopThreat });
    }
  }
}
